import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, ExternalLinkIcon, FileTextIcon, SearchIcon, ShieldCheckIcon, BookOpenIcon, ClipboardListIcon, BuildingIcon, ArrowRightIcon } from 'lucide-react';
const downloadForms = [{
  title: 'Live Scan Pre-Registration Form',
  description: 'Download and complete this form before your appointment to speed up the fingerprinting process.',
  url: 'https://health.maryland.gov/bswe/Documents/OnlineLicApplications/Livescan%20Pre-RegApp.pdf',
  format: 'PDF',
  icon: FileTextIcon,
  featured: true
}];
const externalResources = [{
  title: 'Authorization Number Lookup',
  description: 'Look up your employer or licensing agency authorization number required for background checks.',
  url: 'https://www.dpscs.state.md.us/publicservs/bgchecks.shtml',
  icon: SearchIcon,
  category: 'Background Checks'
}, {
  title: 'Maryland Licensing Resources',
  description: 'Find licensing requirements and fingerprinting guidelines for various Maryland professions and industries.',
  url: 'https://www.dllr.state.md.us/',
  icon: ClipboardListIcon,
  category: 'Licensing'
}, {
  title: 'CJIS Background Check Portal',
  description: 'Access the Criminal Justice Information System portal for background check status and information.',
  url: 'https://www.dpscs.state.md.us/publicservs/bgchecks.shtml',
  icon: ShieldCheckIcon,
  category: 'Government'
}, {
  title: 'FBI Identity History Summary',
  description: 'Request your FBI criminal history record (Identity History Summary) directly from the FBI.',
  url: 'https://www.fbi.gov/how-can-we-help-you/need/identity-history-summary-checks',
  icon: BuildingIcon,
  category: 'Federal'
}, {
  title: 'Maryland State Police - CJIS',
  description: 'Official Maryland State Police CJIS page with forms, fee schedules, and processing information.',
  url: 'https://mdsp.maryland.gov/Organization/Pages/CriminalInvestigationBureau/CJISCentralRepository.aspx',
  icon: BookOpenIcon,
  category: 'Government'
}, {
  title: 'TSA Hazmat Endorsement',
  description: 'Information for commercial drivers seeking Hazardous Materials Endorsement background checks.',
  url: 'https://www.tsa.gov/for-industry/hazmat-endorsement',
  icon: FileTextIcon,
  category: 'Licensing'
}];
export function Resources() {
  const externalLinks = [{
    name: 'CJIS Website',
    url: 'https://www.dpscs.state.md.us/publicservs/bgchecks.shtml'
  }, {
    name: 'FBI Identity History',
    url: 'https://www.fbi.gov/how-we-can-help-you/more-fbi-services-and-information/identity-history-summary-checks'
  }, {
    name: 'Maryland State Police',
    url: 'https://mdsp.maryland.gov/'
  }, {
    name: 'Health Maryland',
    url: 'https://health.maryland.gov/'
  }, {
    name: 'CJIS Fingerprinting Fees',
    url: 'https://www.dpscs.state.md.us/publicservs/fingerprint.shtml'
  }, {
    name: 'Maryland COMAR Regulations',
    url: 'http://www.dsd.state.md.us/COMAR/ComarHome.html'
  }];
  return <section id="resources" className="py-12 lg:py-20 bg-white border-b border-slate-100" aria-labelledby="resources-heading" data-id="element-192">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-193">
        <div className="text-center mb-16 lg:mb-20" data-id="element-194">
          <h2 id="resources-heading" className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight" data-id="element-195">
            Resources & Forms
          </h2>
          <div className="w-16 h-1.5 bg-[#4AA6CF] mx-auto rounded-full mb-8" aria-hidden="true" data-id="element-196"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8" data-id="element-197">
          {/* Featured Download */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-slate-50 rounded-3xl p-8 lg:p-10 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500" data-id="element-198">
            <div className="flex flex-col md:flex-row gap-8 items-center" data-id="element-199">
              <div className="bg-white p-6 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100" data-id="element-200">
                <DownloadIcon className="h-12 w-12 text-[#4AA6CF]" data-id="element-201" />
              </div>
              <div className="flex-grow text-center md:text-left" data-id="element-202">
                <h3 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight" data-id="element-203">
                  Live Scan Pre-Registration Form
                </h3>
                <p className="text-slate-600 mb-8 leading-relaxed text-base" data-id="element-204">
                  Download and complete this form before your appointment to
                  speed up the process.
                </p>
                <a href="https://health.maryland.gov/bswe/Documents/OnlineLicApplications/Livescan%20Pre-RegApp.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-full font-bold hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl" data-id="element-205">
                  <DownloadIcon className="h-5 w-5" data-id="element-206" />
                  Download PDF Form
                </a>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-3xl p-8 lg:p-10 border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500" data-id="element-207">
            <h3 className="text-2xl font-extrabold text-slate-900 mb-8 tracking-tight" data-id="element-208">
              External Resources
            </h3>
            <ul className="space-y-4" data-id="element-209">
              {externalLinks.map((link, i) => <li key={i} data-id="element-210">
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between group text-slate-600 hover:text-[#4AA6CF] transition-colors font-semibold text-base" data-id="element-211">
                    {link.name}
                    <ArrowRightIcon className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" data-id="element-212" />
                  </a>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}